import { CorporateActionDto } from 'widgets/corpActions/api/CorporateActionsResponse';
import { v4 as uuid } from 'uuid';
import { HolidayDto } from 'widgets/corpActions/api/HolidaysResponse';
import { SymbolEvent } from 'widgets/corpActions/data/SymbolEvent';
import { groupBy } from 'lodash';
import uniq from 'lodash/uniq';
import { getEnumValue } from '@instinet-analytics/core-lib';
import { HolidayType } from 'widgets/corpActions/data/HolidayType';
import { DateTime } from 'luxon';

export function mapCorporateActionDto(dto: CorporateActionDto): SymbolEvent {
    let effectiveDate =
        dto.caType === 'EARN_RLS'
            ? fromIsoDate(dto.expectedRptDate)
            : fromIsoDate(dto.exDate);
    if (effectiveDate === undefined) {
        effectiveDate = fromIsoDate(dto.announcementDate);
    }

    return {
        uuid: uuid(),
        effectiveDate,
        caId: dto.caId,
        bloombergSymbol: dto.bbCode,
        symbol: dto.symbol,
        type: dto.caType,
        announcementDate: fromIsoDate(dto.announcementDate),
        exDate: fromIsoDate(dto.exDate),
        notes: dto.notes,
        amdDate: fromIsoDate(dto.amdDate),
        oldCusip: dto.oldCusip,
        newCusip: dto.newCusip,
        oldIsin: dto.oldIsin,
        newIsin: dto.newIsin,
        oldSedol: dto.oldSedol,
        newSedol: dto.newSedol,
        oldExch: dto.oldExch,
        newExch: dto.newExch,
        oldName: dto.oldName,
        newName: dto.newName,
        oldRlot: dto.oldRlot,
        newRlot: dto.newRlot,
        oldTicker: dto.oldTicker,
        newTicker: dto.newTicker,
        delistExch: dto.delistExch,
        delistReason: codeOrLabel(dto.delistReason, dto.delistReasonLabel),
        recordDate: fromIsoDate(dto.recordDate),
        payDate: fromIsoDate(dto.payDate),
        adjustmentFactor: dto.adjustmentFactor,
        dividendCurrency: dto.dividendCurrency,
        dividendGrossAmount: dto.dividendGrossAmount,
        stockTicker: dto.stockTicker,
        offerType: codeOrLabel(dto.offerType, dto.offerTypeLabel),
        shareOffer: dto.shareOffer,
        amountOffer: dto.amountOffer,
        offerPrice: dto.offerPrice,
        offerCurrency: dto.offerCurrency,
        listingExch: dto.listingExch,
        listingSecType: codeOrLabel(
            dto.listingSecType,
            dto.listingSecTypeLabel
        ),
        expectedRptDate: fromIsoDate(dto.expectedRptDate),
        expectedRptType: dto.expectedRptType,
        capitalChangeType: codeOrLabel(
            dto.capitalChangeType,
            dto.capitalChangeTypeLabel
        ),
        dividendFreq: dto.dividendFreq,
        bonusAmount: dto.bonusAmount,
        ccRatio: dto.ccRatio,
        ccTerms: dto.ccTerms,
        ccPrice: dto.ccPrice,
        ccTicker2: dto.ccTicker2,
        ccCurrency: dto.ccCurrency,
        tradeStartDate: dto.tradeStartDate,
        tradeEndDate: dto.tradeEndDate,
        expectedRptTime: dto.expectedRptTime,
        expectedRptTimestamp: dto.expectedRptTimestamp,
        adType: codeOrLabel(dto.adType, dto.adTypeLabel),
        adCash: dto.adCash,
        adShares: dto.adShares,
        adStat: codeOrLabel(dto.adStat, dto.adStatLabel),
        adCurrency: dto.adCurrency,
        adAcquirerTicker: dto.adAcquirerTicker,
        adTargetTicker: dto.adTargetTicker,
        adFlag: codeOrLabel(dto.adFlag, dto.adFlagLabel),
        adDateFlag: codeOrLabel(dto.adDateFlag, dto.adDateFlagLabel),
        adPctOwned: dto.adPctOwned,
        adPctSought: dto.adPctSought,
        adUnsolicited: dto.adUnsolicited,
        adUnit: dto.adUnit,
        adAcquiredBy: dto.adAcquiredBy,
        adAmt: dto.adAmt,
        adCashFlag: codeOrLabel(dto.adCashFlag, dto.adCashFlagLabel),
        adSharesFlag: codeOrLabel(dto.adSharesFlag, dto.adSharesFlagLabel),
        bankruptcyType: codeOrLabel(
            dto.bankruptcyType,
            dto.bankruptcyTypeLabel
        ),
        newsRecordDate: dto.newsRecordDate,
        meetingType: codeOrLabel(dto.meetingType, dto.meetingTypeLabel),
        ppId: dto.ppId,
        ppInstallAmt: dto.ppInstallAmt,
        ppInstallDate: dto.ppInstallDate,
        ppInstallType: codeOrLabel(dto.ppInstallType, dto.ppInstallTypeLabel),
        oldClass: codeOrLabel(dto.oldClass, dto.oldClassLabel),
        newClass: codeOrLabel(dto.newClass, dto.newClassLabel),
        reClassType: codeOrLabel(dto.reClassType, dto.reclassTypeLabel),
        reclassTerms: dto.reclassTerms,
        reclassRatio: dto.reclassRatio,
        sbAmt: dto.sbAmt,
        sbType: codeOrLabel(dto.sbType, dto.sbTypeLabel),
        sbCompletedDate: dto.sbCompletedDate,
        sbPrice: dto.sbPrice,
        sbShares: dto.sbShares,
        sbValuePurchased: dto.sbValuePurchased,
        sbCurrency: dto.sbCurrency,
        oldCurrency: dto.oldCurrency,
        newCurrency: dto.newCurrency,
        oldCountry: dto.oldCountry,
        newCountry: dto.newCountry,
    };
}

export function mapHolidayDtosToEvents(dtos: HolidayDto[]): SymbolEvent[] {
    const groups = groupBy(
        dtos,
        (h) => `${h.date}-${h.country}-${h.description}`
    );

    const ret: SymbolEvent[] = [];

    for (const group of Object.values(groups)) {
        const event: SymbolEvent = {
            uuid: uuid(),
            effectiveDate: fromIsoDate(group[0].date),
            type: 'H',
            holidayCountry: group[0].country,
            holidayFlag: group[0].flag,
            holidayDescription: group[0].description,
            holidayType: uniq(
                group.map((g) => getEnumValue(HolidayType, g.type))
            )
                .sort()
                .join(', '),
        };
        ret.push(event);
    }

    return ret;
}

function fromIsoDate(isoDate?: string): number | undefined {
    try {
        return isoDate ? DateTime.fromISO(isoDate).toMillis() : undefined;
    } catch {
        return undefined;
    }
}

function codeOrLabel(
    code: string | number | undefined,
    label: string | undefined
): string | undefined {
    if (label) {
        return label;
    } else {
        return code?.toString();
    }
}
